import router from 'next/router'
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { LOCALSTORAGE_KEY_NEWSLETTER_DIALOG } from '../../constants'
import dynamicHydrate from '../../dynamic-hydration/dynamic-hydrate'
import { useDetectBot } from '../../hooks/useDetectBot'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { getNextMonthDate } from '../../utils/date'
import { useConfig } from '../config'
import { useStore } from '../store'

const NewsletterDialog = dynamicHydrate(() => import('@bluheadless/ui/src/organisms/newsletter/newsletter-dialog'), {
	on: ['idle'],
})

const NewsletterContext = createContext()

const NewsletterProvider = ({ children }) => {
	const { code: storeCode } = useStore()
	const [open, setOpenDialog] = useState(false)
	const [email, setEmail] = useState('')
	const [source, setSource] = useState('')

	const [storageValue, setStorageValue] = useLocalStorage(LOCALSTORAGE_KEY_NEWSLETTER_DIALOG, false)

	const { newsletter: { enabled, popup: { autoOpen, disabledPaths, openTimeout, showPopupInPageViewed } } = {} } =
		useConfig()

	const { isBot, loading: loadingBotCheck } = useDetectBot()

	const setOpen = (value, source = 'Default') => {
		setOpenDialog(value)
		setSource(source)
	}

	const onClose = () => {
		setStorageValue(true, getNextMonthDate())
		setOpenDialog(false)
	}

	const delayNewsletterRef = useRef({
		active: false,
		renderCount: 0,
	})

	useEffect(() => {
		delayNewsletterRef.current.renderCount += 1
		if (
			!loadingBotCheck &&
			!isBot &&
			!storageValue &&
			enabled &&
			autoOpen &&
			openTimeout &&
			!disabledPaths.find((path) => router?.asPath?.match(path)) &&
			delayNewsletterRef.current.renderCount >= showPopupInPageViewed
		) {
			clearTimeout(delayNewsletterRef.current.active)

			delayNewsletterRef.current.active = setTimeout(() => {
				setOpen(true)
				setSource('PopUp')
			}, openTimeout)
		} else if (delayNewsletterRef.current.active) {
			clearTimeout(delayNewsletterRef.current.active)
		}
	}, [
		autoOpen,
		delayNewsletterRef,
		disabledPaths,
		enabled,
		isBot,
		loadingBotCheck,
		openTimeout,
		showPopupInPageViewed,
		storageValue,
	])

	const contextValue = useMemo(
		() => [
			{ open, email },
			{ setOpen, setEmail },
		],
		[email, open]
	)

	return (
		<NewsletterContext.Provider value={contextValue}>
			<>
				{enabled && !!storeCode && (
					<NewsletterDialog onClose={onClose} open={open} source={source} forceHydration={open} />
				)}
				{children}
			</>
		</NewsletterContext.Provider>
	)
}
export default NewsletterProvider

export const useNewsletterContext = () => {
	const newsletterContextValue = useContext(NewsletterContext)
	if (!newsletterContextValue) {
		console.warn(
			`Newsletter context value is missing. Verify that NewsletterProvider is a parent of the current component.`
		)
	}

	return newsletterContextValue
}
